import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { Link } from "@sitecore-jss/sitecore-jss-react";
import { getOverallSettingsClasses, componentId, mobileIs } from "../../utils/siteUtils";
import { Icon } from "../../elements";
import "./style.scss";
import { apiAsia } from "../../utils/api-asia";
import { useTranslation } from "react-i18next";
import customMapStyle from "./style.json";
import { hereApiKey } from "../../config/here-maps";

const FindDealer = props => {
    const { rendering, fields } = props;
    const {
        countryList,
        language,
        languageOpt,
        searchIcon,
        emailIcon,
        phoneIcon,
        navigationIcon,
        pageSize,
        testdriveLink,
        minZoom,
        markerIcon,
        markerIconSelected,
        normalMarkerIcon,
        centerLatitude,
        centerLongitude,
    } = fields;

    const isMobile = mobileIs();
    const mapRef = useRef(null);
    const { t } = useTranslation();
    const apiKey = hereApiKey;

    const contactUsTitle = t('FindDealer_DetailContactUsTitle') || 'Contáctanos';
    const locationTitle = t('FindDealer_DetailLocationListTitle') || 'Ubicación';
    const searchResultTitle = t('FindDealer_DetailLocationTitle') || 'Ubicación';
    const openingHours = t('FindDealer_DetailOpeningHoursTitle') || 'Horario de apertura';
    const exceptStatutoryHolidays = t('FindDealer_ExceptStatutoryHolidays') || 'Excepto días festivos oficiales';
    const fridayShort = t('FindDealer_FridayShort') || 'Vie';
    const getDirections = t('FindDealer_GetDirections') || 'Encuentra la dirección';
    const getLocationFailedMsg = t('FindDealer_GetLocationFailedTooltip') || 'Encontrar ubicación falló';
    const locationInputPlaceholder = t('FindDealer_LocationSelectorPlaceholder') || 'Información del distribuidor';
    const mondayShort = t('FindDealer_MondayShort') || 'Lun';
    const noResultsFound = t('FindDealer_NoResultsFound') || 'Sin resultados';
    const officialCenter = t('FindDealer_OfficialCenter') || 'Centro oficial';
    const open24Hours = t('FindDealer_Open24Hours') || 'Abierto 24 horas';
    const openDaily = t('FindDealer_OpenDaily') || 'Abierto todos los días';
    const partnerCenter = t('FindDealer_PartnerCenter') || 'Centro de nuestros socios';
    const saturdayShort = t('FindDealer_SaturdayShort') || 'Sáb';
    const storeListControl = t('FindDealer_StoreListControl') || 'Vista de mapa';
    const storeListHeader = t('FindDealer_StoreListHeader') || 'Zeekr Oficial cerca de mí';
    const summerTime = t('FindDealer_SummerTime') || 'Horario de verano';
    const sundayShort = t('FindDealer_SundayShort') || 'Dom';
    const testDriveButton = t('FindDealer_TestDriveButton') || 'Test Button';
    const thursdayShort = t('FindDealer_ThursdayShort') || 'Jue';
    const tuesdayShort = t('TuesdayShort') || 'Mar';
    const visitWebsite = t('FindDealer_VisitWebsite') || 'Visita el sitio web';
    const wednesdayShort = t('FindDealer_WednesdayShort') || 'Mié';
    const winterTime = t('FindDealer_WinterTime') || 'Horario de invierno';


    const [showDetail, setShowDetail] = useState(false);
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [dealerData, setDealerData] = useState([]);
    const [originalDealerData, setOriginalDealerData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const mapInstanceRef = useRef(null);
    const platformRef = useRef(null);
    const [mapCenter] = useState(() => {
        const defaultCenter = {
            lat: 25.59508478750113,
            lng: -103.40480764598084
        };

        const lat =
            centerLatitude && centerLatitude.value
                ? parseFloat(centerLatitude.value)
                : NaN;
        const lng =
            centerLongitude && centerLongitude.value
                ? parseFloat(centerLongitude.value)
                : NaN;

        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            return { lat, lng };
        }
        return defaultCenter;
    });
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [showStoreList, setShowStoreList] = useState(true);
    const [isStoreListOpened, setIsStoreListOpened] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const markersRef = useRef({});
    const [displayMode, setDisplayMode] = useState("single");
    const defaultMinZoom = Number(minZoom?.value ?? 3);
    const [currentZoom, setCurrentZoom] = useState(defaultMinZoom);
    const activeClusterMarkersRef = useRef([]);
    const zoomOutButtonRef = useRef(null);
    const [isExpandingCluster, setIsExpandingCluster] = useState(false);

    const dealersRef = useRef([]);
    const searchResultMarkerRef = useRef(null);

    useEffect(() => {
        dealersRef.current = dealerData;
    }, [dealerData]);

    const selectedDealerRef = useRef(null);

    useEffect(() => {
        selectedDealerRef.current = selectedDealer;
    }, [selectedDealer]);

    const suppressNextRefreshRef = useRef(false);

    let lang = language?.fields?.key?.value;
    let langOpt = languageOpt?.fields?.key?.value ?? "english";
    let countryCode = countryList[0]?.fields?.leadsCountryAbbr?.value;

    const getDealer = async () => {
        apiAsia.form
            .dealer({
                language: lang,
                languageOpt: langOpt,
                countryCode: countryCode,
                showOptDetails: true,
                // driveType: 0,只获取售前类型经销商
                pageSize: pageSize?.value ?? "200"
            })
            .then(res => {
                if (!!res[0]) {
                    setDealerData(res[0]);
                    setOriginalDealerData(res[0]);
                }
            })
            .catch(err => {
                console.error(err);
            });
    };

    const getGridSize = zoom => {
        if (zoom <= 5) return 2.0;
        if (zoom <= 8) return 1.0;
        if (zoom <= 10) return 0.5;
        return 0.1;
    };

    const getDistanceKm = (lat1, lng1, lat2, lng2) => {
        const toRad = value => (value * Math.PI) / 180;
        const R = 6371;

        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const radLat1 = toRad(lat1);
        const radLat2 = toRad(lat2);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(radLat1) * Math.cos(radLat2) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    const createSingleMarker = dealer => {
        const lat = parseFloat(dealer.latitude);
        const lng = parseFloat(dealer.longitude);

        const isSelected =
            selectedDealerRef.current && selectedDealerRef.current.id === dealer.id;
        const elementId = isSelected ? "red-marker" : "black-marker";

        const element = document.getElementById(elementId);

        if (!element) return;

        const icon = new window.H.map.DomIcon(element, {
            size: { w: 32, h: 32 },
            anchor: { x: 16, y: 32 }
        });

        const marker = new window.H.map.DomMarker(
            {
                lat,
                lng
            },
            { icon }
        );

        marker.addEventListener("tap", () => {
            handleShowDetail(dealer, { fromMapMarker: true });
        });

        marker.isSingleMarker = true;
        mapInstanceRef.current.addObject(marker);

        if (dealer.id) {
            markersRef.current[dealer.id] = marker;
        }
    };

    const createClusterMarker = (cluster, zoom) => {
        const clusterSize = cluster.count;
        const clusterKey = cluster.key;

        const element = document.createElement("div");
        element.className = "cluster-marker";
        element.innerHTML = `<span>${clusterSize}</span>`;

        let size = 35;
        let fontSize = "12px";
        let bgColor = "#000";

        if (clusterSize > 20) {
            size = 55;
            fontSize = "16px";
        } else if (clusterSize > 10) {
            size = 50;
            fontSize = "14px";
        } else if (clusterSize > 5) {
            size = 45;
            fontSize = "13px";
        } else {
            size = 40;
            fontSize = "12px";
        }

        element.style.cssText = `
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background-color: ${bgColor};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${fontSize};
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        border: 2px solid white;
        cursor: pointer;
    `;

        const icon = new window.H.map.DomIcon(element, {
            size: { w: size, h: size },
            anchor: { x: size / 2, y: size / 2 }
        });

        const marker = new window.H.map.DomMarker(
            {
                lat: cluster.center.lat,
                lng: cluster.center.lng
            },
            { icon }
        );

        marker.addEventListener("tap", () => {
            if (!mapInstanceRef.current) return;

            const currentZoom = mapInstanceRef.current.getZoom();
            const zoomStep = 1.5;
            const maxZoom = 15;
            const targetZoom = Math.min(currentZoom + zoomStep, maxZoom);

            const viewModel = mapInstanceRef.current.getViewModel();
            viewModel.setLookAtData(
                {
                    position: {
                        lat: cluster.center.lat,
                        lng: cluster.center.lng
                    },
                    zoom: targetZoom
                },
                true
            );
        });

        mapInstanceRef.current.addObject(marker);
        activeClusterMarkersRef.current.push(marker);

        marker.clusterData = cluster;
        marker.clusterKey = clusterKey;
    };

    const createGridClustering = dealers => {
        if (!window.H || !mapInstanceRef.current || !dealers.length) {
            return;
        }

        const zoom = mapInstanceRef.current.getZoom();

        const objectsToRemove = [];
        const allObjects = mapInstanceRef.current.getObjects();

        allObjects.forEach(obj => {
            if (
                (obj instanceof window.H.map.Marker || obj instanceof window.H.map.DomMarker) &&
                !obj.isSearchMarker
            ) {
                objectsToRemove.push(obj);
            }
        });

        objectsToRemove.forEach(obj => {
            try {
                mapInstanceRef.current.removeObject(obj);
            } catch (error) {
                console.warn("移除标记时出错:", error);
            }
        });

        markersRef.current = {};
        activeClusterMarkersRef.current = [];

        const gridSize = getGridSize(zoom);
        const grid = {};

        dealers.forEach(dealer => {
            if (!dealer.latitude || !dealer.longitude) return;

            const lat = parseFloat(dealer.latitude);
            const lng = parseFloat(dealer.longitude);

            const gridX = Math.floor(lat / gridSize);
            const gridY = Math.floor(lng / gridSize);
            const gridKey = `${gridX},${gridY}`;

            if (!grid[gridKey]) {
                grid[gridKey] = {
                    dealers: [],
                    centerLat: 0,
                    centerLng: 0,
                    count: 0,
                    key: gridKey
                };
            }

            grid[gridKey].dealers.push(dealer);
            grid[gridKey].centerLat += lat;
            grid[gridKey].centerLng += lng;
            grid[gridKey].count++;
        });

        Object.keys(grid).forEach(key => {
            const cell = grid[key];
            const centerLat = cell.centerLat / cell.count;
            const centerLng = cell.centerLng / cell.count;

            let showAsSingle = false;

            if (cell.count === 1) {
                showAsSingle = true;
            } else if (zoom >= 15) {
                showAsSingle = true;
            } else if (zoom >= 13) {
                showAsSingle = cell.count <= 20;
            } else if (zoom >= 11) {
                showAsSingle = cell.count <= 5;
            }

            if (showAsSingle) {
                cell.dealers.forEach(dealer => {
                    if (dealer.latitude && dealer.longitude) {
                        createSingleMarker(dealer);
                    }
                });
            } else {
                const cluster = {
                    center: { lat: centerLat, lng: centerLng },
                    dealers: cell.dealers,
                    count: cell.count,
                    key: cell.key
                };

                createClusterMarker(cluster, zoom);
            }
        });

        if (zoom < 11) {
            setDisplayMode("cluster");
        } else {
            setDisplayMode("single");
        }
    };

    const updateMapDisplay = () => {
        if (!window.H || !mapInstanceRef.current) {
            return;
        }

        const zoom = mapInstanceRef.current.getZoom();
        setCurrentZoom(zoom);

        if (suppressNextRefreshRef.current) {
            suppressNextRefreshRef.current = false;
            return;
        }

        const dealers = dealersRef.current;
        if (!dealers || !dealers.length) {
            return;
        }


        createGridClustering(dealers);
    };

    const clearSingleMarkers = () => {
        const allObjects = mapInstanceRef.current.getObjects();
        allObjects.forEach(obj => {
            if (obj instanceof window.H.map.DomMarker && !obj.clusterData) {
                try {
                    mapInstanceRef.current.removeObject(obj);
                } catch (error) {
                    console.warn("移除单个标记时出错:", error);
                }
            }
        });
        markersRef.current = {};
    };

    const resetAllMarkers = () => {
        Object.keys(markersRef.current).forEach(dealerId => {
            updateMarkerStyle(dealerId, false);
        });
    };

    const handleMarkerClick = dealer => {
        resetAllMarkers();
        if (dealer.id && markersRef.current[dealer.id]) {
            updateMarkerStyle(dealer.id, true);
        }
        handleShowDetail(dealer);
    };

    const updateMarkerStyle = (dealerId, isActive) => {
        if (!window.H || !mapInstanceRef.current || !markersRef.current[dealerId]) return;

        const marker = markersRef.current[dealerId];
        const elementId = isActive ? "red-marker" : "black-marker";
        const element = document.getElementById(elementId);

        if (!element) return;

        const icon = new window.H.map.DomIcon(element, {
            size: { w: 32, h: 32 },
            anchor: { x: 16, y: 32 }
        });

        marker.setIcon(icon);
    };

    const searchPlaces = query => {
        if (!platformRef.current || !query.trim()) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);

        const at = '0,0';
        const lang = "en";
        const limit = 100;
        const url = `https://autosuggest.search.hereapi.com/v1/autosuggest?at=${at}&lang=${lang}&q=${encodeURIComponent(
            query
        )}&limit=${limit}&apiKey=${apiKey}`;

        fetch(url)
            .then(response => response.json())
            .then(result => {
                setIsSearching(false);

                if (result.items) {
                    setSearchResults(result.items);
                } else if (result.suggestions) {
                    setSearchResults(result.suggestions);
                } else {
                    setSearchResults([]);
                }

                const firstResult =
                    (result.items && result.items[0]) ||
                    (result.suggestions && result.suggestions[0]);
                if (firstResult && firstResult.position && mapInstanceRef.current) {
                    mapInstanceRef.current.setCenter({
                        lat: firstResult.position.lat,
                        lng: firstResult.position.lng
                    });
                    mapInstanceRef.current.setZoom(14);
                }
            })
            .catch(error => {
                setIsSearching(false);
                setSearchResults([]);
            });
    };

    const handleInputFocus = () => {
        setIsInputFocused(true);
        setShowSearchResults(true);
        setShowStoreList(false);
    };

    const handleInputBlur = () => {
        setTimeout(() => {
            setIsInputFocused(false);
            setShowSearchResults(false);
            setShowStoreList(true);

            if (!searchTerm.trim()) {
                if (searchResultMarkerRef.current && mapInstanceRef.current) {
                    try {
                        mapInstanceRef.current.removeObject(
                            searchResultMarkerRef.current
                        );
                    } catch (e) {
                    }
                    searchResultMarkerRef.current = null;
                }
                setSelectedLocation(null);
                if (originalDealerData && originalDealerData.length) {
                    setDealerData(originalDealerData);
                }
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.setCenter(mapCenter);
                    mapInstanceRef.current.setZoom(defaultMinZoom);
                }
            }
        }, 700);
    };

    const handleSearchChange = event => {
        const value = event.target.value;
        setSearchTerm(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        if (!value.trim()) {
            setSearchResults([]);
            setShowSearchResults(false);
            setShowStoreList(true);
            return;
        }
        setShowSearchResults(true);
        setShowStoreList(false);
        const timeout = setTimeout(() => {
            searchPlaces(value);
        }, 500);

        setSearchTimeout(timeout);
    };

    const handleSearchResultClick = result => {
        if (result.position && mapInstanceRef.current) {
            const { lat, lng } = result.position;

            const currentZoom = mapInstanceRef.current.getZoom();
            const targetZoom = currentZoom < 10 ? 10 : currentZoom;

            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(10);

            if (searchResultMarkerRef.current) {
                try {
                    mapInstanceRef.current.removeObject(
                        searchResultMarkerRef.current
                    );
                } catch (e) {
                }
                searchResultMarkerRef.current = null;
            }

            if (window.H && window.H.map && window.H.map.Marker && window.H.map.Icon) {
                const svgMarkup = normalMarkerIcon?.value?.alt ? normalMarkerIcon?.value?.alt :
                    '<svg class="icon" width="40" height="40" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg"><path fill="#d81e06" d="M512 85.333333c141.226667 0 256 113.493333 256 253.866667C768 529.493333 512 810.666667 512 810.666667S256 529.493333 256 339.2C256 198.826667 370.773333 85.333333 512 85.333333m0 170.666667a85.333333 85.333333 0 0 0-85.333333 85.333333 85.333333 85.333333 0 0 0 85.333333 85.333334 85.333333 85.333333 0 0 0 85.333333-85.333334 85.333333 85.333333 0 0 0-85.333333-85.333333m341.333333 554.666667c0 94.293333-152.746667 170.666667-341.333333 170.666666s-341.333333-76.373333-341.333333-170.666666c0-55.04 52.053333-104.106667 132.693333-135.253334l27.306667 38.826667C284.586667 733.44 256 759.893333 256 789.333333c0 58.88 114.773333 106.666667 256 106.666667s256-47.786667 256-106.666667c0-29.44-28.586667-55.893333-74.666667-75.093333l27.306667-38.826667C801.28 706.56 853.333333 755.626667 853.333333 810.666667z"  /></svg>';

                const icon = new window.H.map.Icon(svgMarkup);

                const searchMarker = new window.H.map.Marker(
                    { lat, lng },
                    { icon }
                );
                searchMarker.isSearchMarker = true;

                mapInstanceRef.current.addObject(searchMarker);
                searchResultMarkerRef.current = searchMarker;
            }

            const addressLabel = result.address.label;
            setSelectedLocation({
                label: addressLabel,
                position: result.position
            });
            setSearchTerm(addressLabel);
            setSearchResults([]);
            setShowSearchResults(false);
            setShowStoreList(true);

            if (dealerData && dealerData.length) {
                const refLat = lat;
                const refLng = lng;

                const sortedDealers = dealerData
                    .map(dealer => {
                        const dLat = parseFloat(dealer.latitude);
                        const dLng = parseFloat(dealer.longitude);
                        let distance = Infinity;

                        if (
                            !Number.isNaN(dLat) &&
                            !Number.isNaN(dLng)
                        ) {
                            distance = getDistanceKm(refLat, refLng, dLat, dLng);
                        }

                        return {
                            ...dealer,
                            _distanceFromSearch: distance
                        };
                    })
                    .sort((a, b) => a._distanceFromSearch - b._distanceFromSearch);

                setDealerData(sortedDealers);
            }
        }
    };

    const showDealersOnMapSimple = dealers => {
        if (!window.H || !mapInstanceRef.current || !dealers.length) return;

        clearSingleMarkers();

        dealers.forEach(dealer => {
            if (dealer.latitude && dealer.longitude) {
                createSingleMarker(dealer);
            }
        });

        setDisplayMode("single");
    };

    const clearClusterMarkers = (specificMarker = null) => {
        if (specificMarker) {
            try {
                mapInstanceRef.current.removeObject(specificMarker);
                const markerIndex = activeClusterMarkersRef.current.indexOf(specificMarker);
                if (markerIndex > -1) {
                    activeClusterMarkersRef.current.splice(markerIndex, 1);
                }
            } catch (error) {
                console.warn("移除指定集群标记时出错:", error);
            }
        } else {
            activeClusterMarkersRef.current.forEach(marker => {
                try {
                    mapInstanceRef.current.removeObject(marker);
                } catch (error) {
                    console.warn("移除集群标记时出错:", error);
                }
            });
            activeClusterMarkersRef.current = [];
        }
    };

    const initZoomControlButtons = () => {
        const zoomButtons = document.querySelectorAll(
            "#map .H_l_bottom.H_l_right .H_btn.H_el"
        );
        if (zoomButtons && zoomButtons.length >= 2) {
            zoomOutButtonRef.current = zoomButtons[1];
        }
    };

    const updateZoomControlState = zoom => {
        const zoomOutBtn = zoomOutButtonRef.current;
        if (!zoomOutBtn) return;

        const min = defaultMinZoom;
        if (!Number.isNaN(min) && zoom <= min) {
            zoomOutBtn.classList.add("zoom-minus-disabled");
        } else {
            zoomOutBtn.classList.remove("zoom-minus-disabled");
        }
    };

    const setupZoomListener = map => {
        if (!map) return () => { };

        const onMapViewChangeEnd = () => {
            if (!mapInstanceRef.current) return;

            let zoom = mapInstanceRef.current.getZoom();

            if (!Number.isNaN(defaultMinZoom) && zoom < defaultMinZoom) {
                zoom = defaultMinZoom;
                mapInstanceRef.current.setZoom(defaultMinZoom);
            }

            updateZoomControlState(zoom);
            updateMapDisplay();
        };

        map.addEventListener("mapviewchangeend", onMapViewChangeEnd);

        return () => {
            map.removeEventListener("mapviewchangeend", onMapViewChangeEnd);
        };
    };

    useEffect(() => {
        getDealer();

        if (!window.H || !window.H.service) {
            return;
        }

        const platform = new window.H.service.Platform({
            apikey: apiKey
        });
        platformRef.current = platform;

        const defaultLayers = platform.createDefaultLayers();

        if (
            defaultLayers &&
            defaultLayers.vector &&
            defaultLayers.vector.normal &&
            defaultLayers.vector.normal.map &&
            typeof defaultLayers.vector.normal.map.setMin === "function"
        ) {
            defaultLayers.vector.normal.map.setMin(defaultMinZoom);
        }

        const map = new window.H.Map(
            document.getElementById("map"),
            defaultLayers.vector.normal.map,
            {
                center: mapCenter,
                zoom: currentZoom,
                pixelRatio: window.devicePixelRatio || 1
            }
        );
        mapInstanceRef.current = map;

        try {
            const style = new window.H.map.render.harp.Style(customMapStyle);
            const vectorLayer = platform.getOMVService().createLayer(style);

            if (vectorLayer && typeof vectorLayer.setMin === "function") {
                vectorLayer.setMin(defaultMinZoom);
            }

            map.setBaseLayer(vectorLayer);

            const baseLayer = map.getBaseLayer && map.getBaseLayer();
            if (baseLayer && typeof baseLayer.setMin === "function") {
                baseLayer.setMin(defaultMinZoom);
            }
        } catch (e) {
            const baseLayer = map.getBaseLayer && map.getBaseLayer();
            if (baseLayer && typeof baseLayer.setMin === "function") {
                baseLayer.setMin(defaultMinZoom);
            }
        }

        const cleanupZoomListener = setupZoomListener(map);

        window.addEventListener("resize", () => map.getViewPort().resize());
        new window.H.mapevents.Behavior(
            new window.H.mapevents.MapEvents(map)
        );
        const ui = window.H.ui.UI.createDefault(map, defaultLayers, "en-US");

        setTimeout(() => {
            const mapSettingsControl = ui.getControl("mapsettings");
            if (mapSettingsControl) {
                mapSettingsControl.setVisibility(false);
            }
            const zoomControl = ui.getControl("zoom");
            if (zoomControl) {
                zoomControl.setAlignment("top-bottom");
            }
            const searchControl = ui.getControl("search");
            if (searchControl) {
                searchControl.setAlignment("top-right");
            }

            initZoomControlButtons();
            if (mapInstanceRef.current) {
                updateZoomControlState(mapInstanceRef.current.getZoom());
            }
        }, 100);

        return () => {
            cleanupZoomListener();
            if (map) {
                map.dispose();
            }
            mapInstanceRef.current = null;
            platformRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (dealerData.length > 0 && mapInstanceRef.current) {
            updateMapDisplay();
        }
    }, [dealerData]);

    const handleLocationClick = () => {
        if (mapInstanceRef.current && !isLoadingLocation) {
            setIsLoadingLocation(true);

            setTimeout(() => {
                mapInstanceRef.current.setCenter(mapCenter, false);
                setIsLoadingLocation(false);
            }, 1000);
        }
    };

    const handleShowDetail = (dealer, options = {}) => {
        const { fromMapMarker = false } = options;
        setSelectedDealer(dealer);
        setShowDetail(true);

        const hasMarker = !!(dealer.id && markersRef.current[dealer.id]);
        if (hasMarker) {
            resetAllMarkers();
            updateMarkerStyle(dealer.id, true);
        }

        if (!fromMapMarker && dealer.latitude && dealer.longitude && mapInstanceRef.current) {
            const currentZoom = mapInstanceRef.current.getZoom();
            const targetZoom = Math.max(currentZoom, 15);

            suppressNextRefreshRef.current = hasMarker;

            mapInstanceRef.current.setCenter({
                lat: parseFloat(dealer.latitude),
                lng: parseFloat(dealer.longitude)
            });
            mapInstanceRef.current.setZoom(targetZoom);
        }
    };

    const handleCloseDetail = () => {
        resetAllMarkers();
        setShowDetail(false);
        setSelectedDealer(null);

        const zoom = mapInstanceRef.current.getZoom();
        if (zoom < 10 && displayMode === "single") {
            const allObjects = mapInstanceRef.current.getObjects();
            const objectsToRemove = [];

            allObjects.forEach(obj => {
                if (obj instanceof window.H.map.DomMarker && !obj.clusterData) {
                    objectsToRemove.push(obj);
                }
            });

            objectsToRemove.forEach(obj => {
                try {
                    mapInstanceRef.current.removeObject(obj);
                } catch (error) {
                    console.warn("移除单个标记时出错:", error);
                }
            });

            markersRef.current = {};

            createGridClustering(dealerData);
            setDisplayMode("cluster");
        }
    };

    const rootClasses = classNames(
        "find-dealer",
        getOverallSettingsClasses(fields)
    );
    const rootId = componentId(rendering);

    return (
        <div
            className={rootClasses}
            datasourceid={rendering.dataSource}
            id={rootId}
        >
            <div className="map-wrapper">
                <div className="container">
                    <div ref={mapRef} id="map" className="map"></div>
                    {isLoadingLocation ? (
                        <div className="located">
                            <Icon name="Loading" className="spinner-icon" />
                        </div>
                    ) : (
                        <div className="loading" onClick={handleLocationClick}>
                            <Icon
                                name="Locate"
                                className="icon-store-map-located"
                            />
                        </div>
                    )}
                    <div style={{ display: "none" }}>
                        {markerIcon?.value?.src && (
                            <>
                                <div id="black-marker">
                                    <Icon icon={markerIcon?.value} />
                                </div>
                                <div id="red-marker">
                                    <Icon icon={markerIconSelected?.value} />
                                </div>
                            </>
                        )}
                    </div>
                    <div
                        className={classNames(
                            "search-trigger-container",
                            "full-height",
                            {
                                hide: showDetail
                            }
                        )}
                    >
                        <div className="input-container">
                            <span
                                className={classNames(
                                    "lk-input-affix-wrapper",
                                    "input",
                                    {
                                        "lk-input-affix-wrapper-focused": isInputFocused || !!searchTerm
                                    }
                                )}
                            >
                                <span className="lk-input-prefix">
                                    {searchIcon?.value && (
                                        <Icon
                                            icon={searchIcon.value}
                                            className="search-icon"
                                        />
                                    )}
                                </span>
                                <label
                                    htmlFor="_z_input_1"
                                    className={classNames(
                                        "lk-input-placeholder",
                                        {
                                            "lk-input-placeholder-up": isInputFocused || !!searchTerm
                                        }
                                    )}
                                >
                                    {locationInputPlaceholder}
                                </label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    id="_z_input_1"
                                    className="lk-input lk-input-pl0 lk-input-pr0"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onFocus={handleInputFocus}
                                    onBlur={handleInputBlur}
                                />
                            </span>

                            {showSearchResults && searchResults.length > 0 && (
                                <div className="search-results-dropdown">
                                    <p className="title">{searchResultTitle}</p>
                                    {searchResults.map((result, index) => (
                                        <p
                                            key={index}
                                            className="search-result-item"
                                            onClick={() => {
                                                handleSearchResultClick(result);
                                            }}
                                        >
                                            {result?.title}
                                        </p>
                                    ))}
                                </div>
                            )}
                            {showSearchResults &&
                                !isSearching &&
                                searchResults.length === 0 &&
                                searchTerm && (
                                    <div className="search-results-dropdown">
                                        <p className="title">{searchResultTitle}</p>
                                        <div
                                            className="search-result-item no-results"
                                            onClick={() => {
                                            }}
                                        >
                                            {noResultsFound}
                                        </div>
                                    </div>
                                )}
                        </div>
                        {showStoreList && !isInputFocused && (
                            <div
                                className={classNames("store-list", {
                                    opened: isStoreListOpened
                                })}
                            >
                                {isMobile ? (
                                    <div
                                        className={classNames("header", {
                                            "no-selected-position":
                                                !selectedLocation
                                        })}
                                        onClick={() =>
                                            setIsStoreListOpened(
                                                !isStoreListOpened
                                            )
                                        }
                                    >
                                        <div
                                            className={classNames(
                                                "arrow-down",
                                                {
                                                    active: isStoreListOpened
                                                }
                                            )}
                                        ></div>
                                        {selectedLocation &&
                                            searchTerm === selectedLocation.label && (
                                            <div className="description">
                                                {storeListHeader}&nbsp;
                                                <span>
                                                    {
                                                        selectedLocation?.label
                                                    }
                                                </span>
                                            </div>
                                        )}
                                </div>
                            ) : (
                                selectedLocation &&
                                searchTerm === selectedLocation.label && (
                                    <div className="header">
                                        <div className="description">
                                            {storeListHeader}&nbsp;
                                            <span>
                                                {selectedLocation?.label}
                                            </span>
                                        </div>
                                    </div>
                                )
                            )}
                            <div className="store-content">
                                {dealerData &&
                                    dealerData.map((item, index) => (
                                            <div
                                                className="store-item"
                                                key={index}
                                                onClick={() =>
                                                    handleShowDetail(item)
                                                }
                                            >
                                                <div className="left">
                                                    <div className="h5">
                                                        {item?.name}
                                                    </div>
                                                    <div className="store-operating-time-container body5 time-container">
                                                        {Array.isArray(item?.businessHours) &&
                                                        item.businessHours.length > 0 ? (
                                                            <div className="store-operating-time-container body5">
                                                                <div className="store-operating-time-inner">
                                                                    {item.businessHours.map(
                                                                        (hour, idx) => (
                                                                            <p key={idx}>
                                                                                {hour}
                                                                            </p>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="store-operating-time-inner">
                                                                <div>
                                                                    <p>
                                                                        {openDaily}
                                                                    </p>
                                                                    <p>
                                                                        {open24Hours}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="address-container body5">
                                                        <p>
                                                            {item?.address},
                                                            {item?.city},
                                                            {item?.country}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="right">
                                                    <div className="icon-store-map-show-store-detail-icon"></div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                {isMobile && (
                                    <div className="store-list-control" onClick={() =>
                                        setIsStoreListOpened(false)
                                    }>
                                        <Icon
                                            name="Map"
                                            className="icon-store-map-store-list-control"
                                        />
                                        <span className="body5">
                                            {storeListControl}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div
                        className={classNames("detail-drawer", {
                            show: showDetail
                        })}
                    >
                        <div className="close" onClick={handleCloseDetail}>
                            <Icon name="Close" />
                        </div>
                        <div className="detail-content">
                            <div className="h5">{selectedDealer?.name}</div>
                            <div className="detail-block">
                                <div className="sub-title title2">
                                    {locationTitle}
                                </div>
                                <div className="desc-container">
                                    <div className="desc block">
                                        {selectedDealer?.address},
                                    </div>
                                    <div className="desc block">
                                        {selectedDealer?.city}
                                    </div>
                                    <div className="desc block">
                                        {selectedDealer?.country}
                                    </div>
                                </div>
                            </div>
                            <div className="detail-block">
                                <div className="sub-title title2">
                                    {openingHours}
                                </div>
                                <div className="desc-container">
                                    {Array.isArray(selectedDealer?.businessHours) &&
                                    selectedDealer.businessHours.length > 0 ? (
                                        <div className="store-operating-time-container body5">
                                            <div className="store-operating-time-inner">
                                                {selectedDealer.businessHours.map(
                                                    (hour, index) => (
                                                        <p key={index}>
                                                            {hour}
                                                        </p>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="desc time-desc">
                                            <div className="store-operating-time-container body5">
                                                <div className="store-operating-time-inner">
                                                    <div>
                                                        <div>
                                                            <div>
                                                                <p>
                                                                    {openDaily}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p>
                                                                    {open24Hours}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="detail-block">
                                <div className="sub-title title2">
                                    {contactUsTitle}
                                </div>
                                <ul className="contact-container">
                                    {selectedDealer?.telephone && (
                                        <li className="contact-item">
                                            <div className="icon-wrapper">
                                                <Icon
                                                    className="icon-phone"
                                                    icon={phoneIcon?.value}
                                                />
                                            </div>
                                            <a
                                                href={`tel:${selectedDealer?.telephone}`}
                                                rel="nofollow"
                                            >
                                                {selectedDealer?.telephone}
                                            </a>
                                        </li>
                                    )}
                                    {selectedDealer?.email && (
                                        <li className="contact-item">
                                            <div className="icon-wrapper">
                                                <Icon
                                                    className="icon-email"
                                                    icon={emailIcon?.value}
                                                />
                                            </div>
                                            <a
                                                href={`mailto:${selectedDealer?.email}`}
                                                rel="nofollow"
                                            >
                                                {selectedDealer?.email}
                                            </a>
                                        </li>
                                    )}
                                    {selectedDealer?.latitude &&
                                        selectedDealer?.longitude && (
                                            <li className="contact-item">
                                                <div className="icon-wrapper">
                                                    <Icon
                                                        className="icon-navigation"
                                                        icon={
                                                            navigationIcon?.value
                                                        }
                                                    />
                                                </div>
                                                <a
                                                    target="_blank"
                                                    href={`https://www.google.com/maps/search/?api=1&query=${selectedDealer?.latitude}%2C${selectedDealer?.longitude}`}
                                                    rel="nofollow"
                                                >
                                                    {getDirections}
                                                </a>
                                            </li>
                                        )}
                                </ul>
                            </div>
                        </div>
                        {testdriveLink?.value && (
                            <div className="button-group">
                                <Link field={testdriveLink.value}>
                                    <button
                                        className="lk-btn lk-btn-primary lk-btn-round body5"
                                        type="button"
                                    >
                                        <span>{testDriveButton}</span>
                                    </button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FindDealer;